import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import { prefetchReviewOnce, REVIEW_FRESH_MS, reviewImageUrls } from './review-prefetch';
import { draftKind, type DraftKind, type DraftPreview } from './draft-preview';
import { NO_DRAFT_STATE, readDraftState, type DraftDelivery, type DraftState } from './draft-state';

class DraftReadFailed extends Error {
  constructor(readonly status: number) {
    super(`draft-preview ${status}`);
  }
}

export async function fetchDraftPreview(workspaceId: string, taskId: string, draftId: string, kind: DraftKind): Promise<DraftPreview> {
  const query = new URLSearchParams({ workspaceId, taskId, draftId, kind });
  const res = await apiFetch(`${API_BASE}/api/draft-preview?${query.toString()}`, { credentials: 'include' });
  if (!res.ok) throw new DraftReadFailed(res.status);
  const body = (await res.json().catch(() => null)) as Partial<Record<keyof DraftPreview, unknown>> | null;
  if (!body || typeof body.html !== 'string' || !body.html) throw new DraftReadFailed(res.status);
  return {
    html: body.html,
    kind: draftKind(body.kind),
    title: typeof body.title === 'string' ? body.title : '',
    subject: typeof body.subject === 'string' ? body.subject : '',
    preheader: typeof body.preheader === 'string' ? body.preheader : '',
  };
}

export async function fetchDraftState(workspaceId: string, taskId: string, draftId: string, kind: DraftKind): Promise<DraftState> {
  const query = new URLSearchParams({ workspaceId, taskId, draftId, kind });
  const res = await apiFetch(`${API_BASE}/api/draft-state?${query.toString()}`, { credentials: 'include' });
  if (res.status === 404) return NO_DRAFT_STATE;
  if (!res.ok) throw new DraftReadFailed(res.status);
  return readDraftState(await res.json().catch(() => null));
}

export const DRAFT_STATE_POLL_FIRST_MS = 2_000;
export const DRAFT_STATE_POLL_MAX_MS = 30_000;
export const DRAFT_STATE_POLL_WINDOW_MS = 5 * 60_000;

export interface DraftStatePoll {
  at: number;
  deliveredAtTap: DraftDelivery | null;
  scheduledAtTap?: string | null;
}

export function draftStatePollDelayMs(attempt: number): number {
  return Math.min(DRAFT_STATE_POLL_FIRST_MS * 2 ** Math.min(Math.max(attempt, 0), 4), DRAFT_STATE_POLL_MAX_MS);
}

export function draftDeliveryLanded(atTap: DraftDelivery | null | undefined, now: DraftDelivery | null | undefined): boolean {
  return !!now && (!atTap || now.url !== atTap.url || now.scheduled !== atTap.scheduled || now.at !== atTap.at);
}

export function draftStatePollMs(poll: DraftStatePoll | null, state: DraftState | undefined, now: number): number | false {
  if (!poll || draftDeliveryLanded(poll.deliveredAtTap, state?.delivered)) return false;
  if (state?.scheduled && state.scheduled.at !== (poll.scheduledAtTap ?? null)) return false;
  const elapsed = now - poll.at;
  let next = 0;
  for (let attempt = 0; next <= elapsed; attempt++) next += draftStatePollDelayMs(attempt);
  return next <= DRAFT_STATE_POLL_WINDOW_MS ? next - elapsed : false;
}

export function useDraftState(ids: DraftIds, enabled: boolean, poll: DraftStatePoll | null) {
  return useQuery({
    queryKey: queryKeys.draftState(ids.workspaceId, ids.taskId, ids.draftId),
    queryFn: () => fetchDraftState(ids.workspaceId, ids.taskId, ids.draftId, ids.kind),
    enabled,
    staleTime: 0,
    retry: false,
    refetchInterval: (data?: DraftState) => draftStatePollMs(poll, data, Date.now()),
  });
}

type DraftIds = { workspaceId: string; taskId: string; draftId: string; kind: DraftKind };

const retryDraftRead = (count: number, error: unknown) =>
  count < 1 && !(error instanceof DraftReadFailed && error.status >= 400 && error.status < 500);

export function useDraftPreview(ids: DraftIds | null, revision?: string) {
  return useQuery({
    queryKey: queryKeys.draftPreview(ids?.workspaceId ?? '', ids?.taskId ?? '', ids?.draftId ?? '', revision),
    queryFn: () => fetchDraftPreview(ids?.workspaceId ?? '', ids?.taskId ?? '', ids?.draftId ?? '', ids?.kind ?? 'EMAIL'),
    enabled: Boolean(ids),
    staleTime: revision ? REVIEW_FRESH_MS : 0,
    retry: retryDraftRead,
  });
}

function draftInlineQuery(ids: DraftIds, cardKey: string) {
  return {
    queryKey: queryKeys.draftInlinePreview(ids.workspaceId, ids.taskId, ids.draftId, cardKey),
    queryFn: () => fetchDraftPreview(ids.workspaceId, ids.taskId, ids.draftId, ids.kind),
    staleTime: Infinity,
    cacheTime: 30 * 60_000,
    retry: retryDraftRead,
  };
}

export function useDraftInlinePreview(ids: DraftIds, cardKey: string, enabled: boolean) {
  return useQuery({ ...draftInlineQuery(ids, cardKey), enabled });
}

export function prefetchDraftReview(ids: DraftIds, revision: string): void {
  if (!revision) return;
  prefetchReviewOnce(
    queryClient,
    {
      queryKey: queryKeys.draftPreview(ids.workspaceId, ids.taskId, ids.draftId, revision),
      queryFn: () => queryClient.fetchQuery(draftInlineQuery(ids, revision)),
    },
    (data: DraftPreview) => reviewImageUrls(data.html),
  );
}
